import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import KpiCard from '../components/KpiCard';
import { exportTableAsExcel } from '../lib/export';
import { fmtMoney, fmtInt } from '../lib/fmt';
import { textAccentClass } from '../lib/ui';
import type { ControlePedidosOut, FornecedorControleOut } from '../types/api';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtPct1(n: number) {
  return `${n.toFixed(1)}%`;
}

function cumprColor(v: number): string {
  if (v >= 99) return 'emerald-300';
  if (v >= 90) return 'mbc-bluel';
  if (v >= 70) return 'amber-300';
  return 'rose-300';
}

function ltColor(v: number | null): string {
  if (v === null) return 'mbc-mute';
  if (v <= 7)  return 'emerald-300';
  if (v <= 15) return 'mbc-bluel';
  if (v <= 30) return 'amber-300';
  return 'rose-300';
}

function ltBarColor(v: number): string {
  if (v <= 7)  return 'bg-emerald-400';
  if (v <= 15) return 'bg-mbc-bluel';
  if (v <= 30) return 'bg-amber-400';
  return 'bg-rose-400';
}

// ── CSV export ────────────────────────────────────────────────────────────────

function exportExcel(data: ControlePedidosOut) {
  const headers = [
    'Fornecedor','Qtd Pedidos','Valor Pedidos','Valor NFs','% Cumprimento',
    'Diff R$','Bonificação R$','Qtd Bonificações','Lead Time Médio (d)','NFs com Lead Time',
    'NFs Divergentes','NFs Conferidas','Pedidos sem NF','Recebidos Total',
  ];
  const rows = data.fornecedores.map(r => [
    r.forn,
    r.qtd_ped,
    r.valor_ped.toFixed(2),
    r.valor_nf.toFixed(2),
    r.cumprimento.toFixed(1),
    r.diff_valor.toFixed(2),
    r.bonif_total.toFixed(2),
    r.qtd_bonif,
    r.lt_avg !== null ? r.lt_avg.toFixed(1) : '',
    r.lt_n,
    r.nfs_diverg,
    r.nfs_conf,
    r.sem_nf,
    r.recebidos,
  ]);
  exportTableAsExcel(
    `MBC_controle_pedidos_${new Date().toISOString().slice(0, 10)}.xlsx`,
    headers,
    rows,
  );
}

// ── Sort helper ───────────────────────────────────────────────────────────────

type SortKey = keyof FornecedorControleOut;

function sortRows(rows: FornecedorControleOut[], key: SortKey, dir: 'asc' | 'desc') {
  return [...rows].sort((a, b) => {
    const av = a[key] ?? (dir === 'desc' ? -Infinity : Infinity);
    const bv = b[key] ?? (dir === 'desc' ? -Infinity : Infinity);
    if (av < bv) return dir === 'asc' ? -1 : 1;
    if (av > bv) return dir === 'asc' ? 1 : -1;
    return 0;
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SortTh({
  label, sortKey, current, dir, onSort, cls = '',
}: {
  label: string; sortKey: SortKey; current: SortKey; dir: 'asc' | 'desc';
  onSort: (k: SortKey) => void; cls?: string;
}) {
  const active = current === sortKey;
  return (
    <th
      className={`cursor-pointer select-none hover:text-mbc-soft ${cls}`}
      onClick={() => onSort(sortKey)}
    >
      {label}
      {active && <span className="ml-1 text-mbc-orange">{dir === 'desc' ? '↓' : '↑'}</span>}
    </th>
  );
}

function EmptyState({ msg, color = 'emerald' }: { msg: string; color?: string }) {
  return (
    <div className={`text-center py-6 text-sm ${textAccentClass(`${color}-300`, 'text-emerald-300')}`}>{msg}</div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ControlePedidos() {
  const { data, isLoading, isError } = useQuery<ControlePedidosOut>({
    queryKey: ['controle-pedidos'],
    queryFn: () => api.get('/pedidos/controle').then(r => r.data),
  });

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('valor_ped');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const rows = useMemo(() => {
    if (!data) return [];
    const filtered = search
      ? data.fornecedores.filter(r => r.forn.toLowerCase().includes(search.toLowerCase()))
      : data.fornecedores;
    return sortRows(filtered, sortKey, sortDir);
  }, [data, search, sortKey, sortDir]);

  function handleSort(k: SortKey) {
    if (k === sortKey) setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(k); setSortDir('desc'); }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-mbc-mute text-sm">
        Carregando controle de pedidos…
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="card p-6 text-rose-300 text-sm">
        Erro ao carregar dados. Verifique se há pedidos aprovados cadastrados.
      </div>
    );
  }

  const { kpis, pedidos_deficit, nfs_divergentes, top_bonif, lt_ranking, pareto } = data;

  const cumprKpiColor = kpis.cumpr_geral >= 95 ? 'emerald-300' : kpis.cumpr_geral >= 80 ? 'amber-300' : 'rose-300';
  const ltKpiColor = ltColor(kpis.lt_med);
  const totBonifForns = new Set(top_bonif.map(r => r.forn)).size;

  // Pareto: find how many suppliers = 80%
  const pareto80 = pareto.findIndex(r => r.pct_acc >= 80);
  const paretoCount = pareto80 >= 0 ? pareto80 + 1 : pareto.length;
  const paretoTotal = fmtMoney(kpis.tot_valor_ped);

  return (
    <div className="space-y-6 fade-in">

      {/* ── KPIs linha 1 ── */}
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard
          label="Pedidos aprovados"
          value={fmtInt(kpis.tot_pedidos)}
          sub={`${kpis.forns_ativos} fornecedores`}
          accent="mbc-orangel"
          icon="📋"
        />
        <KpiCard
          label="Cumprimento de faturamento"
          value={fmtPct1(kpis.cumpr_geral)}
          sub={`${fmtMoney(kpis.tot_valor_nf)} de ${fmtMoney(kpis.tot_valor_ped)}`}
          accent={cumprKpiColor}
          icon="📊"
        />
        <KpiCard
          label="Bonificação total"
          value={fmtMoney(kpis.tot_bonif)}
          sub={`${kpis.lt_n > 0 ? fmtInt(kpis.tot_bonif > 0 ? top_bonif.reduce((s, r) => s + r.qtd_bonif, 0) : 0) : '0'} NFs · ${totBonifForns} fornecedores`}
          accent={kpis.tot_bonif > 0 ? 'emerald-300' : 'mbc-mute'}
          icon="🎁"
        />
        <KpiCard
          label="Lead time médio (real)"
          value={kpis.lt_med !== null ? `${kpis.lt_med.toFixed(1)}d` : '—'}
          sub={`${kpis.lt_n} NFs com recebimento registrado`}
          accent={ltKpiColor}
          icon="⏱️"
        />
      </div>

      {/* ── KPIs linha 2 ── */}
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard
          label="Déficit fornecedor"
          value={fmtMoney(kpis.valor_deficit)}
          sub={`${kpis.pedidos_deficit} pedidos com NF menor que pedido`}
          accent={kpis.valor_deficit > 0 ? 'rose-300' : 'mbc-mute'}
          icon="⚠️"
        />
        <KpiCard
          label="NFs com divergência"
          value={fmtInt(kpis.tot_diverg)}
          sub="boleto real fora do combinado"
          accent={kpis.tot_diverg > 0 ? 'rose-300' : 'mbc-mute'}
          icon="⚠️"
        />
        <KpiCard
          label="NFs conferidas"
          value={fmtInt(kpis.tot_conferidos)}
          sub="boleto real bate com pedido"
          accent="emerald-300"
          icon="✓"
        />
        <KpiCard
          label="Pedidos sem NF"
          value={fmtInt(kpis.pedidos_sem_nf)}
          sub="aguardando faturamento"
          accent={kpis.pedidos_sem_nf > 0 ? 'amber-300' : 'mbc-mute'}
          icon="⏳"
        />
      </div>

      {/* ── Tabela por fornecedor ── */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="text-white font-bold flex items-center gap-2">🏭 Performance por fornecedor</h3>
            <p className="text-xs text-mbc-mute mt-0.5">Pedidos aprovados × NFs faturadas × bonificação × lead time real.</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar fornecedor…"
              className="bg-mbc-panel2 border border-mbc-line rounded-lg px-3 py-1.5 text-sm text-mbc-soft placeholder:text-mbc-mute/70 w-52"
            />
            <button
              onClick={() => exportExcel(data)}
              className="text-xs bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 px-3 py-2 rounded-lg transition-colors"
            >
              📥 Exportar Excel
            </button>
          </div>
        </div>

        <div className="scroller">
          <table className="dt">
            <colgroup>
              <col style={{ width: '220px' }} />
              <col style={{ width: '80px' }} />
              <col style={{ width: '130px' }} />
              <col style={{ width: '130px' }} />
              <col style={{ width: '105px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '95px' }} />
              <col style={{ width: '90px' }} />
              <col style={{ width: '80px' }} />
              <col style={{ width: '80px' }} />
            </colgroup>
            <thead>
              <tr>
                <SortTh label="Fornecedor"    sortKey="forn"       current={sortKey} dir={sortDir} onSort={handleSort} cls="text-left" />
                <SortTh label="Pedidos"       sortKey="qtd_ped"    current={sortKey} dir={sortDir} onSort={handleSort} cls="num" />
                <SortTh label="Valor pedido"  sortKey="valor_ped"  current={sortKey} dir={sortDir} onSort={handleSort} cls="num" />
                <SortTh label="Valor NF"      sortKey="valor_nf"   current={sortKey} dir={sortDir} onSort={handleSort} cls="num" />
                <SortTh label="% Cumprim."    sortKey="cumprimento" current={sortKey} dir={sortDir} onSort={handleSort} cls="num" />
                <SortTh label="Diff R$"       sortKey="diff_valor" current={sortKey} dir={sortDir} onSort={handleSort} cls="num" />
                <SortTh label="🎁 Bonif."     sortKey="bonif_total" current={sortKey} dir={sortDir} onSort={handleSort} cls="num" />
                <SortTh label="⏱️ Lead"       sortKey="lt_avg"     current={sortKey} dir={sortDir} onSort={handleSort} cls="num" />
                <SortTh label="⚠ Diverg."     sortKey="nfs_diverg" current={sortKey} dir={sortDir} onSort={handleSort} cls="text-center" />
                <SortTh label="✓ Conf."       sortKey="nfs_conf"   current={sortKey} dir={sortDir} onSort={handleSort} cls="text-center" />
                <SortTh label="⏳ s/ NF"      sortKey="sem_nf"     current={sortKey} dir={sortDir} onSort={handleSort} cls="text-center" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center text-mbc-mute py-8 text-sm">
                    {search ? 'Nenhum fornecedor encontrado.' : 'Nenhum pedido aprovado encontrado.'}
                  </td>
                </tr>
              ) : rows.map(r => (
                <tr key={r.forn}>
                  <td>
                    <span className="truncate inline-block max-w-[200px] font-semibold text-white" title={r.forn}>
                      {r.forn}
                    </span>
                  </td>
                  <td className="num">{fmtInt(r.qtd_ped)}</td>
                  <td className="num">{fmtMoney(r.valor_ped)}</td>
                  <td className="num">
                    {r.valor_nf > 0 ? fmtMoney(r.valor_nf) : <span className="text-mbc-mute">—</span>}
                  </td>
                  <td className="num">
                    {r.valor_nf === 0
                      ? <span className="text-mbc-mute">—</span>
                      : <span className={`${textAccentClass(cumprColor(r.cumprimento), 'text-rose-300')} font-bold`}>{fmtPct1(r.cumprimento)}</span>
                    }
                  </td>
                  <td className="num">
                    {r.valor_nf === 0
                      ? <span className="text-mbc-mute">—</span>
                      : Math.abs(r.diff_valor) < 1
                        ? <span className="text-emerald-300">R$ 0</span>
                        : <span className={textAccentClass(r.diff_valor < 0 ? 'rose-300' : 'amber-300', 'text-amber-300')}>
                            {r.diff_valor > 0 ? '+' : ''}{fmtMoney(r.diff_valor)}
                          </span>
                    }
                  </td>
                  <td className="num">
                    {r.bonif_total > 0
                      ? <><span className="text-emerald-300 font-semibold">{fmtMoney(r.bonif_total)}</span><span className="text-[10px] text-mbc-mute"> ({r.qtd_bonif})</span></>
                      : <span className="text-mbc-mute">—</span>
                    }
                  </td>
                  <td className="num">
                    {r.lt_avg !== null
                      ? <><span className={`${textAccentClass(ltColor(r.lt_avg), 'text-mbc-mute')} font-bold`}>{r.lt_avg.toFixed(1)}d</span><span className="text-[10px] text-mbc-mute"> ({r.lt_n})</span></>
                      : <span className="text-mbc-mute text-xs">—</span>
                    }
                  </td>
                  <td className="text-center">
                    {r.nfs_diverg > 0
                      ? <span className="badge bg-rose-500/25 text-rose-300">{r.nfs_diverg}</span>
                      : <span className="text-mbc-mute text-xs">—</span>
                    }
                  </td>
                  <td className="text-center">
                    {r.nfs_conf > 0
                      ? <span className="badge bg-emerald-500/25 text-emerald-300">{r.nfs_conf}</span>
                      : <span className="text-mbc-mute text-xs">—</span>
                    }
                  </td>
                  <td className="text-center">
                    {r.sem_nf > 0
                      ? <span className="badge bg-amber-500/20 text-amber-300">{r.sem_nf}</span>
                      : <span className="text-mbc-mute text-xs">—</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Divergências + Déficit ── */}
      <div className="grid xl:grid-cols-2 gap-6">
        <div className="card p-5">
          <h3 className="text-white font-bold flex items-center gap-2 mb-1">⚠ Pedidos com divergência de data</h3>
          <p className="text-xs text-mbc-mute mb-3">NFs onde o boleto real veio fora do combinado.</p>
          {nfs_divergentes.length === 0
            ? <EmptyState msg="✓ Nenhuma divergência de data registrada." />
            : (
              <div className="overflow-x-auto">
                <table className="dt">
                  <thead>
                    <tr>
                      <th className="text-left" style={{ width: 90 }}>Pedido</th>
                      <th className="text-left">Fornecedor</th>
                      <th className="text-left" style={{ width: 80 }}>NF</th>
                      <th className="num" style={{ width: 110 }}>Valor Pedido</th>
                      <th className="num" style={{ width: 110 }}>Valor NF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nfs_divergentes.map((r, i) => (
                      <tr key={i}>
                        <td><span className="font-mono text-mbc-bluel">{r.pedido}</span></td>
                        <td><span className="truncate inline-block max-w-[160px]" title={r.fornecedor}>{r.fornecedor}</span></td>
                        <td><span className="text-mbc-bluel text-xs">NF {r.nf}</span></td>
                        <td className="num">{fmtMoney(r.valor_ped)}</td>
                        <td className="num">{fmtMoney(r.valor_nf)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </div>

        <div className="card p-5">
          <h3 className="text-white font-bold flex items-center gap-2 mb-1">💸 Faturamento menor que pedido</h3>
          <p className="text-xs text-mbc-mute mb-3">Fornecedor faturou menos mercadoria que o aprovado — risco de pré-aprovação dessa diferença.</p>
          {pedidos_deficit.length === 0
            ? <EmptyState msg="✓ Nenhum déficit de faturamento detectado." />
            : (
              <div className="overflow-x-auto">
                <table className="dt">
                  <thead>
                    <tr>
                      <th className="text-left" style={{ width: 90 }}>Pedido</th>
                      <th className="text-left">Fornecedor</th>
                      <th className="num" style={{ width: 110 }}>Pedido</th>
                      <th className="num" style={{ width: 110 }}>NF</th>
                      <th className="num" style={{ width: 120 }}>Déficit</th>
                      <th className="num" style={{ width: 80 }}>% Fat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pedidos_deficit.map((r, i) => (
                      <tr key={i}>
                        <td><span className="font-mono text-mbc-bluel">{r.numero}</span></td>
                        <td><span className="truncate inline-block max-w-[140px]" title={r.fornecedor}>{r.fornecedor}</span></td>
                        <td className="num">{fmtMoney(r.valor_ped)}</td>
                        <td className="num">{fmtMoney(r.valor_nf)}</td>
                        <td className="num"><span className="text-rose-300 font-bold">{fmtMoney(r.deficit)}</span></td>
                        <td className="num">
                          <span className={textAccentClass(r.pct_fat < 70 ? 'rose-300' : r.pct_fat < 90 ? 'amber-300' : 'mbc-bluel', 'text-mbc-bluel')}>
                            {r.pct_fat.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </div>
      </div>

      {/* ── Top bonificação + Lead time ranking ── */}
      <div className="grid xl:grid-cols-2 gap-6">
        <div className="card p-5">
          <h3 className="text-white font-bold flex items-center gap-2 mb-1">🎁 Top fornecedores por bonificação</h3>
          <p className="text-xs text-mbc-mute mb-3">Ranking dos que mais bonificam.</p>
          {top_bonif.length === 0
            ? <div className="text-center text-mbc-mute py-6 text-sm">Nenhuma bonificação registrada ainda.</div>
            : (
              <div className="space-y-2">
                {top_bonif.map((r, i) => {
                  const maxB = top_bonif[0].bonif_total;
                  const pct = (r.bonif_total / maxB) * 100;
                  return (
                    <div key={r.forn}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-mbc-soft truncate max-w-[60%]" title={r.forn}>
                          <b className="text-mbc-mute">{i + 1}.</b> {r.forn}
                        </span>
                        <span className="text-emerald-300 font-bold">
                          {fmtMoney(r.bonif_total)}{' '}
                          <span className="text-[10px] text-mbc-mute">({r.qtd_bonif} NFs)</span>
                        </span>
                      </div>
                      <div className="h-2 bg-mbc-line rounded overflow-hidden">
                        <div className="h-full bg-emerald-400" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          }
        </div>

        <div className="card p-5">
          <h3 className="text-white font-bold flex items-center gap-2 mb-1">⏱️ Ranking de lead time</h3>
          <p className="text-xs text-mbc-mute mb-3">Tempo médio entre emissão da NF e recebimento da mercadoria.</p>
          {lt_ranking.length === 0
            ? <div className="text-center text-mbc-mute py-6 text-sm">Sem histórico de recebimento ainda. Marque pedidos como "Recebido total" para alimentar.</div>
            : (
              <div className="space-y-2">
                {(() => {
                  const maxLT = Math.max(...lt_ranking.map(r => r.lt_avg));
                  return lt_ranking.map((r, i) => {
                    const pct = (r.lt_avg / maxLT) * 100;
                    const cor = ltColor(r.lt_avg);
                    const barCor = ltBarColor(r.lt_avg);
                    return (
                      <div key={r.forn}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-mbc-soft truncate max-w-[60%]" title={r.forn}>
                            <b className="text-mbc-mute">{i + 1}.</b> {r.forn}
                          </span>
                          <span className={`${textAccentClass(cor, 'text-mbc-mute')} font-bold`}>
                            {r.lt_avg.toFixed(1)}d{' '}
                            <span className="text-[10px] text-mbc-mute">({r.lt_n} NFs)</span>
                          </span>
                        </div>
                        <div className="h-2 bg-mbc-line rounded overflow-hidden">
                          <div className={`h-full ${barCor}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )
          }
        </div>
      </div>

      {/* ── Pareto ── */}
      <div className="card p-5">
        <h3 className="text-white font-bold flex items-center gap-2 mb-1">📈 Concentração de compras (Pareto)</h3>
        <p className="text-xs text-mbc-mute mb-3">Quantos fornecedores representam 80% do valor de pedidos.</p>
        {pareto.length === 0
          ? <div className="text-center text-mbc-mute py-6 text-sm">Sem dados ainda.</div>
          : (
            <>
              <div className="text-xs text-mbc-soft mb-3">
                <b className="text-mbc-orangel">{paretoCount} de {pareto.length} fornecedores</b>{' '}
                concentram 80% das suas compras ({paretoTotal})
              </div>
              <div className="space-y-1">
                {pareto.slice(0, 20).map((r, i) => (
                  <div key={r.forn} className="flex items-center gap-2 text-xs">
                    <span className="text-mbc-mute w-8">{i + 1}.</span>
                    <span className="truncate" style={{ width: 200 }} title={r.forn}>{r.forn}</span>
                    <div className="flex-1 h-2 bg-mbc-line rounded overflow-hidden">
                      <div
                        className={`h-full ${r.pct_acc <= 80 ? 'bg-mbc-orange' : 'bg-mbc-blue'}`}
                        style={{ width: `${r.pct}%` }}
                      />
                    </div>
                    <span className="w-20 text-right text-white font-semibold">{fmtMoney(r.valor_ped)}</span>
                    <span className="w-12 text-right text-mbc-mute">{r.pct.toFixed(1)}%</span>
                    <span className={`w-16 text-right ${r.pct_acc <= 80 ? 'text-mbc-orangel font-semibold' : 'text-mbc-mute'}`}>
                      {r.pct_acc.toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            </>
          )
        }
      </div>

    </div>
  );
}
